import { MonthRow, Scenario, ScheduleResult } from '../../domain/types.js';
import * as balanceChart from './balance-chart.js';
import { BalanceSeries } from './balance-chart.js';
import * as paymentChart from './payment-chart.js';
import * as rateChart from './rate-chart.js';
import { ChartMode } from './frame.js';

export type { ChartMode } from './frame.js';
export type { BalanceSeries } from './balance-chart.js';

export type ChartKind = 'balance' | 'payment' | 'rate';

export const CHART_KINDS: ChartKind[] = ['balance', 'payment', 'rate'];

export const CHART_TITLES: Record<ChartKind, string> = {
  balance: 'Остаток долга',
  payment: 'Состав платежа',
  rate: 'Ставка и события',
};

/** Everything the card charts and the fullscreen chart need. */
export type ChartData = {
  schedule: ScheduleResult;
  scenario: Scenario;
  baselineMonths?: MonthRow[];
  /** Comparison lines for the balance chart (payment and rate charts ignore them). */
  others?: BalanceSeries[];
};

type RenderArgs = ChartData & { mode: ChartMode };

let lastArgs: RenderArgs | null = null;
let resizeObserver: ResizeObserver | null = null;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
const pendingResize = new Set<HTMLElement>();

function containerFor(kind: ChartKind): HTMLElement | null {
  return document.getElementById(`${kind}-chart`);
}

function kindFor(container: HTMLElement): ChartKind | undefined {
  return CHART_KINDS.find(kind => container.id === `${kind}-chart`);
}

/** Rendered svg size as `WxH` in whole pixels, or '' when there is no svg / no layout. */
function measure(container: HTMLElement): string {
  const svg = container.querySelector('svg');
  if (!svg) return '';
  const rect = svg.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return '';
  return `${Math.round(rect.width)}x${Math.round(rect.height)}`;
}

/**
 * Renders one chart. The svg is measured while it is being mounted, before flex layout
 * may stretch it, so after the first paint the size is checked once more and the chart is
 * re-rendered when it changed.
 */
export function renderChart(
  kind: ChartKind,
  container: HTMLElement,
  data: ChartData,
  mode: ChartMode,
  remeasure = true,
): void {
  const { schedule, scenario, baselineMonths, others } = data;
  const months = schedule.months;
  switch (kind) {
    case 'balance':
      balanceChart.render(container, { months, scenario, baselineMonths, others }, mode);
      break;
    case 'payment':
      paymentChart.render(container, { months, baselineMonths }, mode);
      break;
    case 'rate':
      rateChart.render(container, { months, scenario }, mode);
      break;
  }
  container.dataset.renderedSize = measure(container);
  if (remeasure && typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      const size = measure(container);
      if (size && size !== container.dataset.renderedSize) {
        renderChart(kind, container, data, mode, false);
      }
    });
  }
}

/** Renders the three card charts into `#balance-chart`, `#payment-chart`, `#rate-chart`. */
export function renderCharts(data: ChartData, mode: ChartMode = 'compact'): void {
  lastArgs = { ...data, mode };
  CHART_KINDS.forEach(kind => {
    const container = containerFor(kind);
    if (container) renderChart(kind, container, data, mode);
  });
  observeResize();
}

/**
 * Re-renders card charts whose svg size changed (window resize, legend appearing, tab
 * switch on phones). Every container reported during the debounce window is collected and
 * re-checked when the timer fires.
 */
function observeResize(): void {
  if (resizeObserver || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      if (entry.target instanceof HTMLElement) pendingResize.add(entry.target);
    });
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = null;
      const args = lastArgs;
      const targets = Array.from(pendingResize);
      pendingResize.clear();
      if (!args) return;
      targets.forEach(target => {
        const size = measure(target);
        const kind = kindFor(target);
        if (!kind || !size || size === target.dataset.renderedSize) return;
        renderChart(kind, target, args, args.mode, false);
      });
    }, 120);
  });
  CHART_KINDS.forEach(kind => {
    const container = containerFor(kind);
    if (container) resizeObserver?.observe(container);
  });
}

/** Wires the segmented control in `#charts`: sets `data-active-chart` and `aria-selected`. */
export function initChartTabs(section: HTMLElement | null = document.getElementById('charts')): void {
  if (!section) return;
  const tabs = Array.from(section.querySelectorAll<HTMLButtonElement>('.chart-tabs [role="tab"]'));
  if (!tabs.length) return;
  const activate = (kind: string) => {
    section.dataset.activeChart = kind;
    tabs.forEach(tab => {
      const selected = tab.dataset.chart === kind;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
  };
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      const kind = tab.dataset.chart;
      if (kind) activate(kind);
    });
    tab.addEventListener('keydown', event => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      const kind = next.dataset.chart;
      if (kind) activate(kind);
      next.focus();
    });
  });
  activate(section.dataset.activeChart ?? tabs[0].dataset.chart ?? 'balance');
}
